import { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CSVLink } from "react-csv";
import { fetchHostBookings, formatDay, isPast, kindLabel, money } from "src/utils/bookings";
import { openBookingConversation } from "src/utils/chat";

// Every guest booking across the host's homes, hotels, experiences and events.

const KINDS = [
  { key: "", label: "All types" },
  { key: "home", label: "Homes" },
  { key: "hotel", label: "Hotels" },
  { key: "experience", label: "Experiences" },
  { key: "event", label: "Events" },
];

const KIND_ICON = { home: "fas fa-home", hotel: "fas fa-hotel", experience: "fas fa-compass", event: "fas fa-ticket-alt" };

const STATUS_LABEL = {
  confirmed: "Confirmed",
  awaiting_host: "Awaiting you",
  expired: "Expired",
  declined: "Declined",
  cancelled: "Cancelled",
  canceled: "Cancelled",
  refunded: "Refunded",
  pending: "Pending",
};

const when = (b) => (b.end && b.kind !== "event" ? `${formatDay(b.start)} → ${formatDay(b.end)}` : formatDay(b.start));

const party = (b) => {
  if (b.kind === "event") return `${b.guests || 1} × ${b.ticket || "ticket"}`;
  return [b.nights && `${b.nights} night${b.nights === 1 ? "" : "s"}`, b.guests && `${b.guests} guest${b.guests === 1 ? "" : "s"}`]
    .filter(Boolean)
    .join(" · ");
};

const Bookings = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [items, setItems] = useState([]);
  const [failed, setFailed] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(["upcoming", "past", "all"].includes(params.get("tab")) ? params.get("tab") : "upcoming");
  const [kind, setKind] = useState(params.get("type") || "");
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(null);
  const [chatError, setChatError] = useState("");
  const [opening, setOpening] = useState(false);

  useEffect(() => {
    fetchHostBookings()
      .then(({ items: list, failed: sources }) => {
        setItems(list);
        setFailed(sources);
      })
      .finally(() => setLoading(false));
  }, []);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return items
      .filter((b) => tab === "all" || (tab === "past" ? isPast(b) : !isPast(b)))
      .filter((b) => !kind || b.kind === kind)
      .filter(
        (b) =>
          !q ||
          [b.listing, b.guest.name, b.guest.email, b.reference].some((v) => (v || "").toLowerCase().includes(q)),
      )
      .sort((a, b) => {
        const diff = new Date(a.start || 0) - new Date(b.start || 0);
        return tab === "upcoming" ? diff : -diff;
      });
  }, [items, tab, kind, query]);

  const csvRows = visible.map((b) => ({
    Type: kindLabel(b.kind),
    Listing: b.listing,
    Guest: b.guest.name,
    Email: b.guest.email,
    Phone: b.guest.phone,
    Start: formatDay(b.start),
    End: formatDay(b.end),
    Guests: b.guests,
    Amount: b.amount,
    Currency: b.currency,
    Status: STATUS_LABEL[b.status] || b.status,
    Reference: b.reference,
  }));

  const messageGuest = async (b) => {
    setChatError("");
    setOpening(true);
    try {
      const conversation = await openBookingConversation(b.kind, b.id);
      navigate(`/app/messages?conversation=${conversation.id}`);
    } catch (error) {
      setChatError(error?.response?.data?.message || "Couldn't open the chat. Please try again.");
      setOpening(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Bookings | Tubayo Host</title>
      </Helmet>
      <div className="hd-page">
        <div className="hd-page-header">
          <div>
            <h1>Bookings</h1>
            <p>Every reservation across your homes, hotels, experiences and events.</p>
          </div>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <div className="hd-segmented" role="tablist">
              {[
                ["upcoming", "Upcoming"],
                ["past", "Past"],
                ["all", "All"],
              ].map(([key, label]) => (
                <button
                  key={key}
                  type="button"
                  role="tab"
                  aria-selected={tab === key}
                  className={tab === key ? "active" : ""}
                  onClick={() => setTab(key)}
                >
                  {label}
                </button>
              ))}
            </div>
            <CSVLink className="hd-btn" data={csvRows} filename="tubayo-bookings.csv">
              <i className="fas fa-download" /> Export
            </CSVLink>
          </div>
        </div>

        <div className="hd-toolbar">
          {KINDS.map((k) => (
            <button
              key={k.label}
              type="button"
              className={`hd-chip ${kind === k.key ? "active" : ""}`}
              onClick={() => setKind(k.key)}
            >
              {k.label}
            </button>
          ))}
          <label className="hd-search">
            <i className="fas fa-search" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search guest, listing or reference"
              aria-label="Search bookings"
            />
          </label>
        </div>

        {failed.length > 0 && <div className="hd-notice">Couldn't load your {failed.join(", ")} bookings right now.</div>}

        <div className="hd-card" style={{ overflowX: "auto" }}>
          {loading ? (
            [0, 1, 2, 3].map((i) => <div key={i} className="hd-skeleton" />)
          ) : visible.length === 0 ? (
            <div className="hd-empty">
              <i className="far fa-calendar" />
              <h3>{tab === "upcoming" ? "No upcoming bookings" : "No bookings found"}</h3>
              <p>
                {query || kind
                  ? "Try another filter or search."
                  : "When guests book your listings, their reservations appear here."}
              </p>
            </div>
          ) : (
            <table className="hd-table">
              <thead>
                <tr>
                  <th>Guest</th>
                  <th>Listing</th>
                  <th>Dates</th>
                  <th className="hd-hide-sm">Party</th>
                  <th className="hd-hide-sm">Paid</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((b) => (
                  <tr key={b.key} onClick={() => setOpen(b)}>
                    <td>
                      <div className="hd-cell-title">
                        <strong>{b.guest.name}</strong>
                        <span>{b.guest.email || b.guest.phone}</span>
                      </div>
                    </td>
                    <td>
                      <div className="hd-cell-title">
                        <strong>{b.listing}</strong>
                        <span className="hd-kind">
                          <i className={KIND_ICON[b.kind]} /> {kindLabel(b.kind)}
                        </span>
                      </div>
                    </td>
                    <td style={{ whiteSpace: "nowrap" }}>{when(b)}</td>
                    <td className="hd-hide-sm">{party(b)}</td>
                    <td className="hd-hide-sm" style={{ whiteSpace: "nowrap" }}>
                      {money(b.amount, b.currency)}
                    </td>
                    <td>
                      <span className={`hd-status ${b.status}`}>{STATUS_LABEL[b.status] || b.status}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {open && (
        <>
          <div className="hd-drawer-backdrop" onClick={() => setOpen(null)} />
          <aside className="hd-drawer" role="dialog" aria-modal="true" aria-label="Booking details">
            <button type="button" className="hd-icon-btn hd-drawer-close" onClick={() => setOpen(null)} aria-label="Close">
              <i className="fas fa-times" />
            </button>
            <span className={`hd-status ${open.status}`}>{STATUS_LABEL[open.status] || open.status}</span>
            <h2>{open.listing}</h2>
            <span className="hd-kind">
              <i className={KIND_ICON[open.kind]} /> {kindLabel(open.kind)} · {when(open)}
            </span>

            <dl className="hd-details">
              {party(open) && (
                <div>
                  <dt>{open.kind === "event" ? "Tickets" : "Party"}</dt>
                  <dd>{party(open)}</dd>
                </div>
              )}
              <div>
                <dt>Paid</dt>
                <dd>{money(open.amount, open.currency) || "—"}</dd>
              </div>
              {open.method && (
                <div>
                  <dt>Payment method</dt>
                  <dd>{open.method}</dd>
                </div>
              )}
              {open.reference && (
                <div>
                  <dt>Reference</dt>
                  <dd style={{ fontWeight: 400, fontSize: 13 }}>{open.reference}</dd>
                </div>
              )}
              {open.createdAt && (
                <div>
                  <dt>Booked on</dt>
                  <dd>{formatDay(open.createdAt)}</dd>
                </div>
              )}
            </dl>

            <div className="hd-person">
              <span className="hd-avatar-initial" style={{ width: 44, height: 44 }}>
                {open.guest.name.charAt(0).toUpperCase()}
              </span>
              <div>
                <span className="hd-muted">Guest</span>
                <strong>{open.guest.name}</strong>
                {open.guest.phone && <a href={`tel:${open.guest.phone}`}>{open.guest.phone}</a>}
                {open.guest.email && <a href={`mailto:${open.guest.email}`}>{open.guest.email}</a>}
              </div>
            </div>

            {chatError && <p className="hd-error">{chatError}</p>}
            <button type="button" className="hd-btn hd-btn-primary hd-btn-block" onClick={() => messageGuest(open)} disabled={opening}>
              <i className="far fa-comment-dots" /> {opening ? "Opening chat…" : "Message guest"}
            </button>
          </aside>
        </>
      )}
    </>
  );
};

export default Bookings;
